import React, { useState } from 'react';
import { X, CheckCircle2, AlertCircle, Check, Plus } from '../icons';

// ============ TOAST ============
export function Toast({ toast }) {
  if (!toast) return null;
  
  return (
    <div className={`fixed bottom-6 right-6 z-50 flex items-center gap-2 px-4 py-3 rounded-xl shadow-lg animate-slide-up ${
      toast.type === 'success' ? 'bg-emerald-600' : toast.type === 'error' ? 'bg-red-600' : 'bg-lime-400 text-black'
    }`}>
      {toast.type === 'success' && <CheckCircle2 size={18} />}
      {toast.type === 'error' && <AlertCircle size={18} />}
      <span className={toast.type === 'error' || toast.type === 'success' ? 'text-white' : 'text-black'} >
        {toast.message}
      </span>
    </div>
  );
}

// ============ MODAL ============
export function Modal({ title, onClose, children, size = 'md' }) {
  const sizeClasses = {
    sm: 'max-w-md',
    md: 'max-w-lg',
    lg: 'max-w-2xl',
    xl: 'max-w-4xl',
    full: 'max-w-6xl'
  };
  
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={onClose} />
      <div className={`relative w-full ${sizeClasses[size]} max-h-[90vh] overflow-y-auto bg-neutral-900 rounded-2xl shadow-2xl border border-neutral-800`}>
        <div className="sticky top-0 bg-neutral-900 border-b border-neutral-800 px-6 py-4 flex items-center justify-between rounded-t-2xl z-10">
          <h2 className="text-lg font-semibold text-white">{title}</h2>
          <button onClick={onClose} className="p-2 hover:bg-neutral-800 rounded-lg text-neutral-400 hover:text-white transition-colors">
            <X size={20} />
          </button>
        </div>
        {children}
      </div>
    </div>
  );
}

// ============ EMPTY STATE ============
export function EmptyState({ icon: IconComp, title, description, action, onAction }) {
  return (
    <div className="flex flex-col items-center justify-center py-20 px-4">
      <div className="w-20 h-20 rounded-2xl bg-neutral-800/50 flex items-center justify-center mb-5">
        <IconComp size={36} className="text-neutral-600" />
      </div>
      <h3 className="text-xl font-semibold text-white mb-2">{title}</h3>
      <p className="text-sm text-neutral-400 text-center max-w-md mb-5">{description}</p>
      {action && (
        <button onClick={onAction} className="flex items-center gap-2 px-5 py-2.5 bg-lime-400 hover:bg-lime-300 text-black rounded-xl text-sm font-medium transition-colors shadow-lg shadow-lime-400/30">
          <Plus size={18} />{action}
        </button>
      )}
    </div>
  );
}

// ============ BADGE ============
export function Badge({ children, color = 'slate', size = 'sm' }) {
  const colors = {
    slate: 'bg-neutral-700 text-neutral-300',
    blue: 'bg-sky-600/20 text-sky-400',
    green: 'bg-green-600/20 text-green-400',
    amber: 'bg-amber-600/20 text-amber-400',
    red: 'bg-red-600/20 text-red-400',
    purple: 'bg-purple-600/20 text-purple-400',
    lime: 'bg-lime-400/20 text-lime-400',
    cyan: 'bg-cyan-600/20 text-cyan-400',
    emerald: 'bg-emerald-600/20 text-emerald-400',
  };
  
  const sizes = {
    xs: 'px-1.5 py-0.5 text-xs',
    sm: 'px-2 py-0.5 text-xs',
    md: 'px-2.5 py-1 text-sm',
  };
  
  return (
    <span className={`font-medium rounded-lg ${colors[color]} ${sizes[size]}`}>
      {children}
    </span>
  );
}

// ============ TABS ============
export function Tabs({ tabs, active, onChange }) {
  return (
    <div className="flex border-b border-neutral-800">
      {tabs.map(tab => (
        <button
          key={tab.id}
          onClick={() => onChange(tab.id)}
          className={`px-5 py-3 text-sm font-medium border-b-2 transition-colors ${
            active === tab.id
              ? 'border-lime-400 text-white'
              : 'border-transparent text-neutral-400 hover:text-white'
          }`}
        >
          {tab.label}
          {tab.badge > 0 && (
            <span className="ml-2 px-1.5 py-0.5 text-xs bg-neutral-700 rounded-full">
              {tab.badge}
            </span>
          )}
        </button>
      ))}
    </div>
  );
}

// ============ BUTTON ============
export function Button({ 
  children, 
  variant = 'primary', 
  size = 'md', 
  disabled = false, 
  loading = false,
  icon: Icon,
  onClick,
  className = '',
  type = 'button'
}) {
  const variants = {
    primary: 'bg-lime-400 hover:bg-lime-300 text-black shadow-lg shadow-lime-400/30',
    secondary: 'bg-neutral-800 hover:bg-neutral-700 text-white',
    danger: 'bg-red-600 hover:bg-red-700 text-white',
    ghost: 'hover:bg-neutral-800 text-neutral-400 hover:text-white',
    outline: 'border border-neutral-700 hover:bg-neutral-800 text-white',
  };
  
  const sizes = {
    xs: 'px-2.5 py-1.5 text-xs',
    sm: 'px-3 py-2 text-sm',
    md: 'px-4 py-2.5 text-sm',
    lg: 'px-6 py-3 text-base',
  };
  
  return (
    <button
      type={type}
      onClick={onClick}
      disabled={disabled || loading}
      className={`flex items-center justify-center gap-2 rounded-xl font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${variants[variant]} ${sizes[size]} ${className}`}
    >
      {loading ? (
        <div className="w-4 h-4 border-2 border-current border-t-transparent rounded-full animate-spin" />
      ) : Icon ? (
        <Icon size={size === 'lg' ? 20 : 18} />
      ) : null}
      {children}
    </button>
  );
}

// ============ INPUT ============
export function Input({ 
  label, 
  error, 
  className = '', 
  ...props 
}) {
  return (
    <div className={className}>
      {label && (
        <label className="block text-xs text-neutral-500 mb-1.5 font-medium">
          {label}
        </label>
      )}
      <input
        {...props}
        className={`w-full bg-neutral-800 border ${error ? 'border-red-500' : 'border-neutral-700'} rounded-xl px-4 py-2.5 text-white text-sm placeholder-neutral-500 focus:outline-none focus:ring-2 focus:ring-lime-400/50 focus:border-lime-400 transition-colors`}
      />
      {error && <p className="mt-1 text-xs text-red-400">{error}</p>}
    </div>
  );
}

// ============ SELECT ============
export function Select({ 
  label, 
  options, 
  error, 
  placeholder = 'Select...', 
  className = '', 
  ...props 
}) {
  return (
    <div className={className}>
      {label && (
        <label className="block text-xs text-neutral-500 mb-1.5 font-medium">
          {label}
        </label>
      )}
      <select
        {...props}
        className={`w-full bg-neutral-800 border ${error ? 'border-red-500' : 'border-neutral-700'} rounded-xl px-4 py-2.5 text-white text-sm focus:outline-none focus:ring-2 focus:ring-lime-400/50 focus:border-lime-400 transition-colors`}
      >
        <option value="">{placeholder}</option>
        {options.map(opt => (
          <option key={opt.value || opt.id || opt} value={opt.value || opt.id || opt}>
            {opt.label || opt.name || opt}
          </option>
        ))}
      </select>
      {error && <p className="mt-1 text-xs text-red-400">{error}</p>}
    </div>
  );
}

// ============ TEXTAREA ============
export function Textarea({ 
  label, 
  error, 
  className = '', 
  rows = 4,
  ...props 
}) {
  return (
    <div className={className}>
      {label && (
        <label className="block text-xs text-neutral-500 mb-1.5 font-medium">
          {label}
        </label>
      )}
      <textarea
        rows={rows}
        {...props}
        className={`w-full bg-neutral-800 border ${error ? 'border-red-500' : 'border-neutral-700'} rounded-xl px-4 py-3 text-white text-sm placeholder-neutral-500 focus:outline-none focus:ring-2 focus:ring-lime-400/50 focus:border-lime-400 transition-colors resize-none`}
      />
      {error && <p className="mt-1 text-xs text-red-400">{error}</p>}
    </div>
  );
}

// ============ CHECKBOX ============
export function Checkbox({ label, checked, onChange, className = '' }) {
  return (
    <label className={`flex items-center gap-3 cursor-pointer group ${className}`}>
      <button
        type="button"
        onClick={() => onChange(!checked)}
        className={`w-5 h-5 rounded-lg border-2 flex items-center justify-center transition-colors ${
          checked
            ? 'bg-emerald-500 border-emerald-500'
            : 'border-neutral-600 hover:border-neutral-500'
        }`}
      >
        {checked && <Check size={12} className="text-white" />}
      </button>
      {label && (
        <span className="text-sm text-white group-hover:text-neutral-200">
          {label}
        </span>
      )}
    </label>
  );
}

// ============ TOGGLE ============
export function Toggle({ label, enabled, onChange, className = '' }) {
  return (
    <label className={`flex items-center justify-between gap-3 cursor-pointer ${className}`}>
      {label && <span className="text-sm text-white">{label}</span>}
      <button
        type="button"
        onClick={() => onChange(!enabled)}
        className={`relative w-11 h-6 rounded-full transition-colors ${
          enabled ? 'bg-lime-400' : 'bg-neutral-700'
        }`}
      >
        <div
          className={`absolute top-1 w-4 h-4 rounded-full bg-white transition-transform ${
            enabled ? 'left-6' : 'left-1'
          }`}
        />
      </button>
    </label>
  );
}

// ============ AVATAR ============
export function Avatar({ src, name, size = 'md', className = '' }) {
  const [error, setError] = useState(false);
  const initials = name?.split(' ').map(w => w[0]).join('').slice(0, 2).toUpperCase() || '?';
  
  const sizes = {
    xs: 'w-6 h-6 text-xs',
    sm: 'w-8 h-8 text-sm',
    md: 'w-10 h-10 text-sm',
    lg: 'w-12 h-12 text-base',
    xl: 'w-16 h-16 text-lg',
  };
  
  if (!src || error) {
    return (
      <div className={`flex items-center justify-center rounded-xl bg-gradient-to-br from-neutral-700 to-neutral-800 text-neutral-300 font-bold ${sizes[size]} ${className}`}>
        {initials}
      </div>
    );
  }
  
  return (
    <img
      src={src}
      alt={name}
      onError={() => setError(true)}
      className={`rounded-xl object-cover ${sizes[size]} ${className}`}
    />
  );
}

// ============ BRAND LOGO ============
export function BrandLogo({ domain, name, size = 40, className = '' }) {
  const [imgError, setImgError] = useState(false);
  const initials = name?.split(' ').map(w => w[0]).join('').slice(0, 2).toUpperCase() || '??';
  const logoUrl = domain ? `https://www.google.com/s2/favicons?domain=${domain}&sz=128` : null;
  
  if (!domain || imgError) {
    return (
      <div
        className={`flex items-center justify-center rounded-xl bg-gradient-to-br from-neutral-700 to-neutral-800 text-neutral-300 font-bold ${className}`}
        style={{ width: size, height: size, fontSize: size * 0.35 }}
      >
        {initials}
      </div>
    );
  }

  return (
    <img
      src={logoUrl}
      alt={name}
      className={`rounded-xl bg-white object-contain ${className}`}
      style={{ width: size, height: size, padding: size * 0.1 }}
      onError={() => setImgError(true)}
    />
  );
}

// ============ STAT CARD ============
export function StatCard({ label, value, icon: IconComp, color = 'text-lime-400', gradient = 'from-lime-400/20 to-lime-400/5' }) {
  return (
    <div className={`bg-gradient-to-br ${gradient} border border-neutral-800/50 rounded-2xl p-5`}>
      <div className="flex items-center gap-3 mb-3">
        <div className={`p-2.5 rounded-xl bg-neutral-800/50 ${color}`}>
          <IconComp size={18} />
        </div>
      </div>
      <p className="text-3xl font-bold text-white mb-1">{value}</p>
      <p className="text-sm text-neutral-400">{label}</p>
    </div>
  );
}

// ============ PROGRESS BAR ============
export function ProgressBar({ value, max = 100, showLabel = false, color = 'bg-lime-400' }) {
  const percentage = Math.min(100, Math.round((value / max) * 100));
  
  return (
    <div>
      {showLabel && (
        <div className="flex items-center justify-between text-xs mb-1">
          <span className="text-neutral-500">Progress</span>
          <span className={percentage === 100 ? 'text-emerald-400' : 'text-neutral-400'}>
            {percentage}%
          </span>
        </div>
      )}
      <div className="h-1.5 bg-neutral-700/50 rounded-full overflow-hidden">
        <div
          className={`h-full rounded-full transition-all ${percentage === 100 ? 'bg-emerald-500' : color}`}
          style={{ width: `${percentage}%` }}
        />
      </div>
    </div>
  );
}

// ============ DROPDOWN ============
export function Dropdown({ trigger, children, align = 'right' }) {
  const [isOpen, setIsOpen] = useState(false);
  
  return (
    <div className="relative">
      <div onClick={() => setIsOpen(!isOpen)}>{trigger}</div>
      {isOpen && (
        <>
          <div className="fixed inset-0 z-40" onClick={() => setIsOpen(false)} />
          <div className={`absolute z-50 mt-2 ${align === 'right' ? 'right-0' : 'left-0'} min-w-48 bg-neutral-900 border border-neutral-800 rounded-xl shadow-xl py-1`}>
            {children}
          </div>
        </>
      )}
    </div>
  );
}

export function DropdownItem({ children, onClick, danger = false }) {
  return (
    <button
      onClick={onClick}
      className={`w-full px-4 py-2 text-sm text-left transition-colors ${
        danger
          ? 'text-red-400 hover:bg-red-500/10'
          : 'text-neutral-300 hover:bg-neutral-800'
      }`}
    >
      {children}
    </button>
  );
}

// ============ LOADING SPINNER ============
export function LoadingSpinner({ size = 'md', className = '' }) {
  const sizes = {
    sm: 'w-4 h-4',
    md: 'w-8 h-8',
    lg: 'w-12 h-12',
  };
  
  return (
    <div className={`${sizes[size]} border-2 border-lime-400 border-t-transparent rounded-full animate-spin ${className}`} />
  );
}

// ============ LOADING SCREEN ============
export function LoadingScreen() {
  return (
    <div className="fixed inset-0 bg-neutral-950 flex items-center justify-center">
      <div className="text-center">
        <LoadingSpinner size="lg" className="mx-auto mb-4" />
        <p className="text-neutral-400">Loading...</p>
      </div>
    </div>
  );
}

// ============ CONFIRMATION DIALOG ============
export function ConfirmDialog({ 
  isOpen, 
  onClose, 
  onConfirm, 
  title, 
  message, 
  confirmText = 'Confirm', 
  cancelText = 'Cancel',
  danger = false 
}) {
  if (!isOpen) return null;
  
  return (
    <Modal title={title} onClose={onClose} size="sm">
      <div className="p-6">
        <p className="text-neutral-300 mb-6">{message}</p>
        <div className="flex justify-end gap-3">
          <Button variant="secondary" onClick={onClose}>
            {cancelText}
          </Button>
          <Button variant={danger ? 'danger' : 'primary'} onClick={onConfirm}>
            {confirmText}
          </Button>
        </div>
      </div>
    </Modal>
  );
}
